/**
 * Performance Optimization Utilities
 * Family App - Optimized for warm, responsive user experience
 */

import React, { Suspense, memo, useMemo } from 'react';

// Performance monitoring utilities
class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.componentRenderCounts = new Map();
  }
  
  startTiming(label) {
    if (typeof performance !== 'undefined') {
      performance.mark(`${label}-start`);
    }
  }
  
  endTiming(label) {
    if (typeof performance !== 'undefined') {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      
      const entries = performance.getEntriesByName(label);
      if (entries.length > 0) {
        const duration = entries[entries.length - 1].duration;
        this.metrics.set(label, duration);
        return duration;
      }
    }
    return 0;
  }
  
  trackComponentRender(componentName) {
    const count = this.componentRenderCounts.get(componentName) || 0;
    this.componentRenderCounts.set(componentName, count + 1);
  }
  
  getMetrics() {
    return {
      timings: Object.fromEntries(this.metrics),
      renderCounts: Object.fromEntries(this.componentRenderCounts),
      totalComponents: this.componentRenderCounts.size
    };
  }
  
  logPerformanceReport() {
    const metrics = this.getMetrics();
    console.group('🚀 Family App Performance Report');
    console.log('Component Render Counts:', metrics.renderCounts);
    console.log('Timing Metrics (ms):', metrics.timings);
    console.log('Total Tracked Components:', metrics.totalComponents);
    console.groupEnd();
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Enhanced lazy loading with family-friendly fallbacks
export const createOptimizedLazyComponent = (
  importFunc, 
  fallbackComponent = null, 
  componentName = 'Unknown'
) => {
  const LazyComponent = React.lazy(() => {
    performanceMonitor.startTiming(`lazy-load-${componentName}`);
    
    return importFunc()
      .then(module => {
        performanceMonitor.endTiming(`lazy-load-${componentName}`);
        return module;
      })
      .catch(error => {
        console.error(`Failed to load ${componentName}:`, error);
        
        // Provide graceful fallback
        return { 
          default: fallbackComponent || (() => (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              color: 'var(--color-stone-600)',
              background: 'var(--color-stone-50)',
              borderRadius: '12px',
              border: '1px solid var(--color-stone-200)'
            }}>
              <p>This feature is temporarily unavailable.</p>
              <p style={{ fontSize: '14px', marginTop: '0.5rem' }}>
                Please refresh the page or try again later.
              </p>
            </div>
          ))
        };
      });
  });
  
  // Return wrapped component with loading state
  return React.forwardRef((props, ref) => (
    <Suspense fallback={<ComponentLoadingSkeleton componentName={componentName} />}>
      <LazyComponent {...props} ref={ref} />
    </Suspense>
  ));
};

// Family-friendly loading skeleton
const ComponentLoadingSkeleton = ({ componentName }) => (
  <div 
    style={{ 
      padding: '1.5rem',
      background: 'var(--color-stone-50)',
      borderRadius: '12px',
      border: '1px solid var(--color-stone-200)',
      animation: 'pulse 1.5s ease-in-out infinite alternate'
    }}
  >
    <div style={{ 
      height: '1.5rem', 
      background: 'var(--color-stone-200)', 
      borderRadius: '8px', 
      marginBottom: '1rem',
      width: '60%'
    }} />
    <div style={{ 
      height: '1rem', 
      background: 'var(--color-stone-200)', 
      borderRadius: '6px', 
      marginBottom: '0.5rem',
      width: '80%'
    }} />
    <div style={{ 
      height: '1rem', 
      background: 'var(--color-stone-200)', 
      borderRadius: '6px',
      width: '40%'
    }} />
  </div>
);

// Performance-optimized component wrapper
export const withPerformanceTracking = (Component, componentName) => {
  return memo((props) => {
    performanceMonitor.trackComponentRender(componentName);
    
    const memoizedProps = useMemo(() => props, [
      // Only re-memoize if specific props change
      props.children,
      props.className,
      props.variant,
      props.size,
      JSON.stringify(props.data || {})
    ]);
    
    return <Component {...memoizedProps} />;
  });
};

// Bundle splitting utilities for family app features
export const familyFeatureBundles = {
  // Core shared components (always loaded)
  core: () => import('../../components/Shared'),
  
  // Feature-specific bundles
  dashboard: () => import('../../components/Dashboard/DashboardContainer'),
  events: () => import('../../components/Events/EventsContainer'),
  messages: () => import('../../components/Messages/FrictionlessFamilyMessages'),
  allowance: () => import('../../components/Allowance/IncomePage'),
  shopping: () => import('../../components/Shopping/ShoppingDashboard'),
  planning: () => import('../../components/Planning/PlanningInterface'),
  
  // Less critical features (can be lazy loaded)
  settings: () => import('../../components/Settings/ProfileManagement')
};

// Preload critical family features
export const preloadCriticalFeatures = () => {
  if (typeof window !== 'undefined') {
    // Preload dashboard and events as they're most commonly used
    setTimeout(() => {
      familyFeatureBundles.dashboard();
      familyFeatureBundles.events();
    }, 1000); // Delay to not interfere with initial load
  }
};

// Resource hints for better loading
export const addResourceHints = () => {
  if (typeof document !== 'undefined') {
    // Preconnect to any external services
    const preconnectLinks = [
      // Add your external domains here
      // 'https://fonts.googleapis.com',
      // 'https://api.yourservice.com'
    ];
    
    preconnectLinks.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      document.head.appendChild(link);
    });
  }
};

// Memory management for family app
export const cleanupUnusedComponents = () => {
  if (typeof window !== 'undefined') {
    // Clean up any cached lazy components that haven't been used recently
    // This is more advanced and depends on your specific needs
    console.log('🧹 Cleaning up unused components...');
  }
};

// Initialize performance optimizations
export const initializePerformanceOptimizations = () => {
  // Add resource hints
  addResourceHints();
  
  // Preload critical features after initial load
  preloadCriticalFeatures();
  
  // Set up periodic cleanup (every 5 minutes)
  if (typeof window !== 'undefined') {
    setInterval(cleanupUnusedComponents, 5 * 60 * 1000);
    
    // Log performance report in development
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        performanceMonitor.logPerformanceReport();
      }, 10000); // After 10 seconds
    }
  }
};

export default {
  createOptimizedLazyComponent,
  withPerformanceTracking,
  performanceMonitor,
  familyFeatureBundles,
  initializePerformanceOptimizations
};